import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { StopCircle as StopCircleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { poursAPI } from '../../services/pours';
import { apiErrorText } from '../../utils/apiError';
import type { TranslateFn } from '../../utils/relativeTime';
import { SAFE_AREA_BOTTOM } from '../mobile/mobileChrome';

/** The pour a Stop dialog is open for. */
export interface StopPourTarget {
  PourID: string;
  PourCode?: string | null;
  PourName?: string | null;
}

/** POST /pour/{id}/stop's reply (already_stopped, tanks_notified, ...). */
export type StopPourResult = Awaited<ReturnType<typeof poursAPI.stopPour>>;

export interface StopPourDialogProps {
  /** The pour to stop; null closes the dialog. */
  target: StopPourTarget | null;
  /** Cancel, Escape or a backdrop tap. Ignored while stopping. */
  onClose: () => void;
  /** The stop succeeded. The caller closes the dialog (target = null). */
  onStopped: (target: StopPourTarget, result: StopPourResult) => void;
  /** The stop failed; the dialog stays open. Without it the message shows
   *  inside the dialog. */
  onError?: (message: string) => void;
  fullScreen?: boolean;
}

// Below md the buttons are 44px touch targets; from md up they keep
// today's desktop height.
const BUTTON_SX = { minHeight: { xs: 44, md: 'auto' } } as const;

/**
 * The success text after a stop, as the Control Panel shows it today:
 * "Pour was already stopped", "Pour stopped. 2 tanks reset." or
 * "Pour stopped."
 */
export const stopSuccessMessage = (
  result: Pick<StopPourResult, 'already_stopped' | 'tanks_notified'>,
  t: TranslateFn,
): string => {
  if (result.already_stopped) return t('pours.alreadyStopped', 'Pour was already stopped');
  if (result.tanks_notified) {
    return t('pours.stopSuccessWithTanks', {
      defaultValue: 'Pour stopped. {{count}} tank(s) reset.',
      defaultValue_one: 'Pour stopped. {{count}} tank reset.',
      defaultValue_other: 'Pour stopped. {{count}} tanks reset.',
      count: result.tanks_notified,
    });
  }
  return t('pours.stopSuccess', 'Pour stopped.');
};

/**
 * The Stop Pour confirmation: today's Control Panel dialog as one component.
 * When it opens it counts the pour's tank-bound locations (they are reset
 * on stop, so the dialog warns about them), then calls poursAPI.stopPour on
 * confirm and holds the stopping state; while stopping it cannot be closed.
 */
const StopPourDialog: React.FC<StopPourDialogProps> = ({ target, onClose, onStopped, onError, fullScreen = false }) => {
  const { t } = useTranslation();
  // null = still counting (or the count failed to load and no note shows).
  const [tankCount, setTankCount] = useState<number | null>(null);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState('');

  // The last pour shown, so the text stays put while the dialog fades out.
  const shownRef = useRef<StopPourTarget | null>(target);
  if (target) shownRef.current = target;
  const shown = target ?? shownRef.current;

  const pourId = target?.PourID ?? null;
  useEffect(() => {
    if (!pourId) return undefined;
    let cancelled = false;
    setTankCount(null);
    setError('');
    const countTanks = async () => {
      try {
        const locations = await poursAPI.getPourLocations2(pourId);
        const count = (locations ?? []).filter((loc) => loc.TankID != null && loc.TankID !== '').length;
        if (!cancelled) setTankCount(count);
      } catch {
        if (!cancelled) setTankCount(0);
      }
    };
    countTanks();
    return () => {
      cancelled = true;
    };
  }, [pourId]);

  const handleConfirm = async () => {
    if (!target || stopping) return;
    const stopped = target;
    setStopping(true);
    setError('');
    let result: StopPourResult;
    try {
      result = await poursAPI.stopPour(stopped.PourID);
    } catch (err: any) {
      const message = apiErrorText(err, t('mobile.actions.stopFailed', { defaultValue: 'Failed to stop pour' }));
      setStopping(false);
      if (onError) onError(message);
      else setError(message);
      return;
    }
    setStopping(false);
    onStopped(stopped, result);
  };

  return (
    <Dialog
      open={target !== null}
      onClose={() => {
        if (!stopping) onClose();
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableEscapeKeyDown={stopping}
    >
      <DialogTitle sx={fullScreen ? { pt: 'calc(16px + env(safe-area-inset-top, 0px))' } : undefined}>
        {t('pours.stopConfirmTitle', 'Stop Pour')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {t('pours.stopConfirmBody', {
            defaultValue:
              'You are about to stop pour {{code}}.\n\nStopping a pour will close the active monitoring session and automatically archive all associated pour data. The pour status will change from In Progress to Completed, where it will remain available for future reference.\n\nAll recorded information, including temperature data, maturity calculations, graphs, reports and project details, is retained and can be accessed at any time by changing the Pour Status filter to Completed via the control panel.\n\nPlease note: Stopping a pour only changes its status within ConXedge. If the logger remains powered on and installed in the concrete, it will continue recording temperature data until it is removed or switched off.',
            code: shown?.PourCode || shown?.PourName || '',
          })}
        </DialogContentText>
        {tankCount !== null && tankCount > 0 && (
          <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
            {t('pours.stopConfirmTankNote', {
              defaultValue_one: 'Stopping this pour will also reset {{count}} tank-bound location.',
              defaultValue_other: 'Stopping this pour will also reset {{count}} tank-bound locations.',
              count: tankCount,
            })}
          </DialogContentText>
        )}
      </DialogContent>
      {/* Outside the scrolling body, so it shows next to the buttons even
          when the long body text is scrolled. */}
      {error && (
        <Box sx={{ px: 3, pt: 1 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Box>
      )}
      <DialogActions sx={fullScreen ? { pb: `calc(8px + ${SAFE_AREA_BOTTOM})` } : undefined}>
        <Button onClick={onClose} disabled={stopping} sx={BUTTON_SX}>
          {t('pours.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={stopping}
          sx={BUTTON_SX}
          startIcon={stopping ? <CircularProgress size={20} color="inherit" /> : <StopCircleIcon />}
        >
          {stopping ? t('pours.stopping', 'Stopping...') : t('pours.confirmStop', 'Stop Pour')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StopPourDialog;
